import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { apiService } from '../services/api';
import { useAuth } from '@/contexts/AuthContext';
import type { TenantSettings } from '../types/tenant';

const DISABLE_AUTH = import.meta.env.VITE_DISABLE_AUTH === 'true';

const getTenantSettingsErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { status?: number; data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    const detailText = typeof detail === 'string' ? detail : undefined;
    return detailText ? `${fallback}: ${detailText}` : `${fallback}${response?.status ? ` (${response.status})` : ''}`;
  }

  return fallback;
};

export const useTenantSettings = (website: string | null) => {
  // Gate on auth readiness: without a session the request goes out with no
  // Authorization header (apiClient silently skips it) and 401s during the
  // cold-load hydration window. In DISABLE_AUTH dev mode there is no session
  // but the apiClient sends a bypass token, so allow it through there.
  const { session } = useAuth();
  return useQuery({
    queryKey: ['tenantSettings', website],
    queryFn: async () => {
      if (!website) return null;
      return apiService.getTenant(website);
    },
    enabled: !!website && (DISABLE_AUTH || !!session),
    staleTime: 60_000, // 60s — tenant settings rarely change, reduces initial page load burst
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateTenantSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenant: TenantSettings) => apiService.createTenant(tenant),
    onSuccess: (data) => {
      message.success('Tenant settings created successfully');
      queryClient.invalidateQueries({ queryKey: ['tenantSettings', data.website] });
    },
    onError: (error) => {
      message.error(getTenantSettingsErrorMessage(error, 'Failed to create tenant settings'));
      console.error(error);
    },
  });
};

export const useUpdateTenantSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ website, data }: { website: string; data: Partial<TenantSettings> }) =>
      apiService.updateTenant(website, data),
    onSuccess: (data) => {
      message.success('Tenant settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['tenantSettings', data.website] });
    },
    onError: (error) => {
      message.error(getTenantSettingsErrorMessage(error, 'Failed to update tenant settings'));
      console.error(error);
    },
  });
};
